/**
 * Invite every parent on the school's fees roster by email — in one go
 * (Gabriel, 2026-09-29: "Build the bulk invite … through the mail, not SMS").
 *
 * The button lives on Accounts → Parents. It needs the fees add-on to be
 * connected (that is where the roster comes from); without it the button
 * explains itself instead of failing. Preview first (a dry run that sends
 * nothing and shows the counts), then send. Each email is the same /join
 * invite a single parent gets; the server skips accounts that already exist
 * here and anyone invited in the last week. 💸 Email only, under the org's
 * on-demand quota, at most 150 per click.
 */
import React, { useState } from 'react';
import { bulkInviteParents, type BulkInviteResult } from '../../services/paymentsApi';

type Scope = 'paid' | 'all';

export default function ParentsBulkInvite({ orgCode, feesConnected }: { orgCode: string | null; feesConnected: boolean }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<Scope>('paid');
  const [busy, setBusy] = useState<'preview' | 'send' | null>(null);
  const [preview, setPreview] = useState<BulkInviteResult | null>(null);
  const [result, setResult] = useState<BulkInviteResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setPreview(null); setResult(null); setError(null); };

  const runPreview = async () => {
    if (!orgCode) return;
    setBusy('preview'); setError(null); setResult(null);
    const r = await bulkInviteParents(orgCode, scope, true);
    setBusy(null);
    if (!r.ok) { setError(r.message || 'Could not read the fees roster.'); setPreview(null); return; }
    setPreview(r);
  };

  const runSend = async () => {
    if (!orgCode || !preview || !preview.counts || preview.counts.toInvite === 0) return;
    if (!window.confirm(`Send ${preview.counts.toInvite} invite email${preview.counts.toInvite === 1 ? '' : 's'} now?`)) return;
    setBusy('send'); setError(null);
    const r = await bulkInviteParents(orgCode, scope, false);
    setBusy(null);
    if (!r.ok) { setError(r.message || 'Sending failed.'); return; }
    setResult(r);
    setPreview(null);
  };

  const c = preview?.counts;
  const skipped = c ? [
    c.existing ? `${c.existing} already have an account` : '',
    c.recent ? `${c.recent} invited in the last 7 days` : '',
    scope === 'paid' && c.unpaid ? `${c.unpaid} not marked paid` : '',
    c.noEmail ? `${c.noEmail} without an email` : '',
    c.invalidEmail ? `${c.invalidEmail} with a broken email` : '',
    c.duplicate ? `${c.duplicate} duplicate rows` : '',
    c.capped ? `${c.capped} left for the next click (150 per click)` : '',
  ].filter(Boolean) : [];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
      <button
        type="button"
        disabled={!orgCode || !feesConnected}
        onClick={() => { reset(); setOpen(true); }}
        title={feesConnected ? 'Email every parent on the fees roster an invite to the app' : 'Needs the school fees module connected first — PEAR links it from the admin console'}
        style={{
          padding: '8px 14px', borderRadius: 10, border: '1px solid #c7d2fe', background: feesConnected ? '#eef2ff' : '#f8fafc',
          color: feesConnected ? '#3730a3' : '#94a3b8', fontWeight: 600, fontSize: 13, cursor: feesConnected ? 'pointer' : 'not-allowed',
        }}
      >
        ✉️ Invite parents by email
      </button>
      {!feesConnected && (
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Reads the fees roster — connect the school fees module first.</span>
      )}

      {open && (
        <div role="dialog" aria-label="Invite parents by email" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }} onClick={() => setOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Invite parents by email</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              Every parent on the fees roster gets the same invite a single parent gets: a link to sign up, pick the bus and add their children.
              Parents who already have an account, or were invited this week, are skipped.
            </div>

            <div style={{ marginTop: 16, display: 'grid', gap: 8 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#0f172a' }}>
                <input type="radio" name="bulk-invite-scope" checked={scope === 'paid'} onChange={() => { setScope('paid'); reset(); }} />
                Paid parents only
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#0f172a' }}>
                <input type="radio" name="bulk-invite-scope" checked={scope === 'all'} onChange={() => { setScope('all'); reset(); }} />
                Everyone on the roster with an email
              </label>
            </div>

            {error && <div style={{ marginTop: 12, fontSize: 13, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '8px 10px' }}>{error}</div>}

            {c && !result && (
              <div data-testid="bulk-invite-preview" style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#0f172a' }}>
                <div style={{ fontWeight: 700 }}>{c.toInvite} parent{c.toInvite === 1 ? '' : 's'} to invite</div>
                <div style={{ color: '#64748b', marginTop: 2 }}>{c.rows} row{c.rows === 1 ? '' : 's'} on the fees roster{skipped.length ? ` · skipped: ${skipped.join(', ')}` : ''}</div>
                {(preview?.sample || []).length > 0 && (
                  <div style={{ color: '#94a3b8', marginTop: 4, fontSize: 12 }}>e.g. {preview!.sample!.join(', ')}</div>
                )}
              </div>
            )}

            {result && (
              <div data-testid="bulk-invite-result" style={{ marginTop: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: '#14532d' }}>
                <div style={{ fontWeight: 700 }}>{result.sent || 0} invite{(result.sent || 0) === 1 ? '' : 's'} sent</div>
                {(result.failed || result.blocked || result.otherOrg) ? (
                  <div style={{ color: '#9a3412', marginTop: 2 }}>
                    {result.failed ? `${result.failed} failed` : ''}
                    {result.blocked ? `${result.failed ? ' · ' : ''}${result.blocked} held back by today's email quota — try again tomorrow` : ''}
                    {result.otherOrg ? `${result.failed || result.blocked ? ' · ' : ''}${result.otherOrg} belong to another organisation` : ''}
                  </div>
                ) : null}
                {(result.errors || []).slice(0, 5).map((e, i) => (
                  <div key={i} style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>{e.email} — {e.reason}</div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button type="button" onClick={() => setOpen(false)} style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', fontSize: 13, cursor: 'pointer' }}>
                {result ? 'Done' : 'Cancel'}
              </button>
              {!result && (
                <button type="button" onClick={() => void runPreview()} disabled={busy !== null} style={{ padding: '8px 14px', borderRadius: 10, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                  {busy === 'preview' ? 'Counting…' : c ? 'Count again' : 'Preview'}
                </button>
              )}
              {!result && (
                <button type="button" onClick={() => void runSend()} disabled={busy !== null || !c || c.toInvite === 0} style={{ padding: '8px 14px', borderRadius: 10, border: 'none', background: !c || c.toInvite === 0 ? '#cbd5e1' : '#4f46e5', color: '#fff', fontWeight: 700, fontSize: 13, cursor: !c || c.toInvite === 0 ? 'not-allowed' : 'pointer' }}>
                  {busy === 'send' ? 'Sending…' : c && c.toInvite > 0 ? `Send ${c.toInvite} email${c.toInvite === 1 ? '' : 's'}` : 'Send'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
